import useFamaBranding from '../hooks/useFamaBranding';
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Leaf, Lock, Mail } from "lucide-react";

export default function LoginPage() {
  const { brandName, companyName, logoUrl } = useFamaBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const destination = useMemo(() => {
    const queryTarget = new URLSearchParams(location.search || '').get('next') || '';
    let storedTarget = '';
    try { storedTarget = window.sessionStorage.getItem('famapos_post_login_redirect') || ''; } catch (_) {}
    const stateTarget = typeof location.state?.from === 'string' ? location.state.from : '';
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true;
    const candidate = stateTarget || queryTarget || storedTarget || (standalone ? '/stockmovel' : '/');
    return candidate.startsWith('/') && !candidate.startsWith('//') && !candidate.startsWith('/login') ? candidate : '/';
  }, [location.search, location.state]);

  useEffect(() => {
    if (!isAuthenticated) return;
    try { window.sessionStorage.removeItem('famapos_post_login_redirect'); } catch (_) {}
    navigate(destination, { replace: true });
  }, [destination, isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Preenche todos os campos."); return; }
    setLoading(true);
    try {
      const result = await login(email, password);
      if (!result?.success) { setError(result?.error || "Email ou palavra-passe incorretos."); return; }
      try { window.sessionStorage.removeItem('famapos_post_login_redirect'); } catch (_) {}
      navigate(destination, { replace: true });
    } catch (err) {
      setError("Erro ao fazer login.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      {/* Background subtle grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="flex flex-col items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={brandName}
                className="h-24 w-24 rounded-2xl object-contain bg-white p-2 shadow-sm"
              />
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-2xl shadow-lg shadow-green-900/40">
                <Leaf className="w-8 h-8 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-white tracking-tight">{brandName}</h1>
          </div>
          <p className="text-gray-500 text-sm mt-1">{companyName}</p>
        </div>

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-lg font-semibold text-white mb-6">Entrar na conta</h2>

          {error && (
            <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3 mb-5">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  autoComplete="email"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 transition-colors placeholder-gray-600"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Palavra-passe</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl pl-10 pr-11 py-3 text-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 transition-colors placeholder-gray-600"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl py-3 text-sm transition-colors mt-2 shadow-lg shadow-green-900/30"
            >
              {loading ? "A entrar..." : "Entrar"}
            </button>
          </form>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          © {new Date().getFullYear()} {brandName} · Acesso restrito
        </p>
      </div>
    </div>
  );
}
